import React, { useEffect, useState, useMemo } from 'react';

/**
 * COMPONENTE: CUMPLEANOS OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el banner de Cumpleañeros con el diseño y animación de
 * la barra de mensajes de apoyo, más confeti puro en CSS (sin Canvas).
 * ------------------------------------------------------------------
 */
const Cumpleanos = ({ config }) => {
    const {
        visible,
        titleText,
        namesJson,
        scale,
        offsetY,
        titleColor,
        nameColor,
        bgColor,
        showConfetti,
        confettiOpacity,
        activeName,
        logoLeftDataUrl,
        logoRightDataUrl,
        isVertical
    } = config;

    // --- ESTADOS DE FLUJO (Mismo diseño y máquina de estados que Messages.js) ---
    const [flowState, setFlowState] = useState('idle'); // idle, entering, typing, visible, fadingOut
    const [currentName, setCurrentName] = useState('');
    const [displayedText, setDisplayedText] = useState('');
    const [pendingName, setPendingName] = useState(null);

    // 1. Sincronización de visibilidad y cambios de nombre activo
    useEffect(() => {
        if (visible) {
            const nextName = activeName || '¡Felicidades!';
            setFlowState(prev => {
                if (prev === 'idle') {
                    setCurrentName(nextName);
                    return 'entering';
                } else if (prev === 'visible' && currentName !== nextName) {
                    setPendingName(nextName);
                    return 'fadingOut';
                }
                return prev;
            });
        } else {
            setFlowState(prev => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
            setPendingName(null);
        }
    }, [visible, activeName, currentName]);

    // 2. Control de transiciones (Entrada / Salida del banner)
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('typing'), 500); // 0.5s coincide con animación de slideIn
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                if (pendingName) {
                    setCurrentName(pendingName);
                    setPendingName(null);
                    setFlowState('entering');
                } else {
                    setCurrentName('');
                    setFlowState('idle');
                }
                setDisplayedText('');
            }, 500); // 0.5s coincide con animación de slideOut
        }
        return () => clearTimeout(timeout);
    }, [flowState, pendingName]);

    // 3. Efecto Máquina de Escribir para el Nombre (Igual a Messages.js)
    useEffect(() => {
        if (flowState === 'typing' && currentName) {
            let idx = 0;
            setDisplayedText('');
            const interval = setInterval(() => {
                setDisplayedText(currentName.substring(0, idx + 1));
                idx++;
                if (idx >= currentName.length) {
                    clearInterval(interval);
                    setFlowState('visible');
                }
            }, 35);
            return () => clearInterval(interval);
        }
    }, [flowState, currentName]);

    // --- RENDERIZADO DE CONFETI BASADO TOTALMENTE EN CSS (SIN CANVAS) ---
    // Generar partículas una única vez usando useMemo para óptimo rendimiento
    const cssConfettiParticles = useMemo(() => {
        const colors = ['#ff003c', '#00f0ff', '#ffb700', '#25d366', '#a020f0', '#ff007f', '#00ff00', '#ffff00'];
        return Array.from({ length: 65 }).map((_, i) => {
            const width = Math.random() * 8 + 5;
            const height = Math.random() * 12 + 6;
            const leftVal = Math.random() * 100;
            // Retraso aleatorio con valores negativos para que inicie cayendo desde varias alturas de inmediato
            const sweepDelay = Math.random() * 8 - 5;
            return {
                id: i,
                left: `${leftVal}%`,
                color: colors[Math.floor(Math.random() * colors.length)],
                width: `${width}px`,
                height: `${height}px`,
                delay: `${sweepDelay}s`,
                duration: `${Math.random() * 3 + 4}s`,
                tiltDelay: `-${Math.random() * 4}s`
            };
        });
    }, []);

    if (flowState === 'idle') return null;

    return (
        <>
            {/* Hojas de Estilos CSS inyectadas dinámicamente para el Confeti CSS y la escala */}
            <style>{`
                @keyframes fall {
                    0% {
                        transform: translateY(-20px) rotate(0deg);
                    }
                    100% {
                        transform: translateY(105vh) rotate(720deg);
                    }
                }
                @keyframes skewTilt {
                    0%, 100% {
                        transform: rotateY(0deg);
                    }
                    50% {
                        transform: rotateY(180deg);
                    }
                }
                .confetti-css-particle {
                    position: absolute;
                    top: -30px;
                    animation: fall linear infinite;
                }
                .confetti-css-inner {
                    width: 100%;
                    height: 100%;
                    animation: skewTilt ease-in-out infinite alternate;
                }
            `}</style>

            {/* Efecto Confeti CSS (Opacidad configurable) */}
            {showConfetti && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    pointerEvents: 'none',
                    zIndex: 98,
                    overflow: 'hidden',
                    opacity: confettiOpacity !== undefined ? confettiOpacity : 0.8,
                    transition: 'opacity 0.2s ease'
                }}>
                    {cssConfettiParticles.map(p => (
                        <div
                             key={p.id}
                             className="confetti-css-particle"
                             style={{
                                 left: p.left,
                                 width: p.width,
                                 height: p.height,
                                 animationDelay: p.delay,
                                 animationDuration: p.duration
                             }}
                        >
                            <div 
                                className="confetti-css-inner"
                                style={{
                                    backgroundColor: p.color,
                                    animationDuration: p.duration,
                                    animationDelay: p.tiltDelay
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Banner de Cumpleañeros con Animación de Entrada/Salida */}
            <div 
                className="messages" 
                style={{ 
                    bottom: `${offsetY}%`,
                    left: '50%',
                    transform: `translate(-50%, 0) scale(${scale ?? 1.0})`,
                    transformOrigin: 'bottom center',
                    transition: 'bottom 0.3s ease, transform 0.2s ease'
                }}
            >
                {isVertical ? (
                    /* --- DISEÑO VERTICAL --- */
                    <div 
                        className={`messages__banner messages__banner--${flowState}`}
                        style={{ 
                            backgroundColor: bgColor || '#ff003c', 
                            backgroundImage: 'none',
                            //border: `4px solid ${nameColor}`,
                            //borderRadius: '24px',
                            padding: '24px 36px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '320px',
                            // width: '90%',
                            // maxWidth: '700px',
                            boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
                            textAlign: 'center',
                            height: 'auto'
                        }}
                    >
                        <video 
                            src="/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4" 
                            autoPlay 
                            loop 
                            muted 
                            playsInline 
                            className="messages__video" 
                            style={{ borderRadius: '20px' }}
                        />

                        {/* Fila de Logos arriba */}
                        {(logoLeftDataUrl || logoRightDataUrl) && (
                            <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                                {logoLeftDataUrl && <img className="messages__logo messages__logo--left" src={logoLeftDataUrl} alt="Logo Izquierdo" style={{ height: '55px', width: 'auto', objectFit: 'contain' }} />}
                                {logoRightDataUrl && <img className="messages__logo messages__logo--right" src={logoRightDataUrl} alt="Logo Derecho" style={{ height: '55px', width: 'auto', objectFit: 'contain' }} />}
                            </div>
                        )}

                        {/* Textos Apilados */}
                        <div className="messages__content" style={{ gap: '4px', padding: 0, width: '100%' }}>
                            <div 
                                className="messages__title-fluid" 
                                style={{ 
                                    color: titleColor || '#ffffff',
                                    textShadow: `0 0 10px ${bgColor || '#ff003c'}`
                                }}
                            >
                                {titleText}
                            </div>
                            <div 
                                className="messages__text-fluid" 
                                style={{ 
                                    color: nameColor || '#ffffff',
                                    textShadow: `0 0 12px ${bgColor || '#ff003c'}`,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {displayedText}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* --- DISEÑO HORIZONTAL ORIGINAL --- */
                    <div 
                        className={`messages__banner messages__banner--${flowState}`}
                        style={{ 
                            backgroundColor: bgColor || '#ff003c', 
                            backgroundImage: 'none',
                            borderTop: `4px solid ${nameColor}`,
                            borderBottom: `4px solid ${nameColor}`
                        }}
                    >
                        <video 
                            src="/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4" 
                            autoPlay 
                            loop 
                            muted 
                            playsInline 
                            className="messages__video" 
                        />

                        {logoLeftDataUrl && <img className="messages__logo messages__logo--left" src={logoLeftDataUrl} alt="Logo Izquierdo" />}

                        <div className="messages__content">
                            <div 
                                className="messages__title-fluid" 
                                style={{ 
                                    color: titleColor || '#ffffff',
                                    textShadow: `0 0 10px ${bgColor || '#ff003c'}`
                                }}
                            >
                                {titleText}
                            </div>
                            <div 
                                className="messages__text-fluid" 
                                style={{ 
                                    color: nameColor || '#ffffff',
                                    textShadow: `0 0 12px ${bgColor || '#ff003c'}`
                                }}
                            >
                                {displayedText}
                            </div>
                        </div>

                        {logoRightDataUrl && <img className="messages__logo messages__logo--right" src={logoRightDataUrl} alt="Logo Derecho" />}
                    </div>
                )}
            </div>
        </>
    );
};

export { Cumpleanos };
